import {graphqlRequest} from '@shopify/cli-kit/node/api/graphql';
import {
  Logger,
  outputCompleted,
  outputInfo,
} from '@shopify/cli-kit/node/output';

import {Header} from '../utils/utils.js';

import {
  DeploymentConfig,
  DeploymentHooks,
  DeploymentCompletedVerificationError,
} from './types.js';
import {
  DeploymentVerificationDetailsQuery,
  DeploymentVerificationDetailsQueryData,
  DeploymentVerificationDetailsResponse,
  Status,
} from './graphql/deployment-verification-details.js';

interface DeploymentVerificationDetailsOptions {
  config: DeploymentConfig;
  hooks?: DeploymentHooks;
  timeoutInSeconds: number;
  logger: Logger;
}

export async function verifyDeploymentCompleted(
  options: DeploymentVerificationDetailsOptions,
  deploymentId: string,
): Promise<DeploymentVerificationDetailsResponse> {
  const {config, logger, timeoutInSeconds, hooks} = options;
  const variables = {
    deploymentId,
  };

  hooks?.onDeploymentCompletedVerificationStart?.();
  outputInfo('Verifying deployment completed...', logger);

  let attempts = 0;
  const startTime = Date.now();

  const handleInterval = async () => {
    const delay = 3000;
    const elapsedTime = (Date.now() - startTime) / 1000;
    if (elapsedTime + delay / 1000 > timeoutInSeconds) {
      hooks?.onDeploymentCompletedVerificationError?.();
      throw new Error(
        `Deployment not completed after ${timeoutInSeconds} seconds.`,
      );
    }
    attempts++;
    await new Promise((resolve) => setTimeout(resolve, delay));
    return check();
  };

  const check = async (): Promise<DeploymentVerificationDetailsResponse> => {
    const response: DeploymentVerificationDetailsQueryData =
      await graphqlRequest({
        query: DeploymentVerificationDetailsQuery,
        api: 'Oxygen',
        url: `${config.deploymentUrl}/api/v2/admin/graphql`,
        token: config.deploymentToken.accessToken,
        variables,
        addedHeaders: {
          [Header.OxygenNamespaceHandle]: config.deploymentToken.namespace,
        },
      });

    const data = response.deploymentVerificationDetails;
    if (data == null) {
      hooks?.onDeploymentCompletedVerificationError?.();
      throw new DeploymentCompletedVerificationError('Deployment not found');
    }

    if (data.status === Status.Deployed) {
      outputCompleted('Deployment to Oxygen completed successfully', logger);
      hooks?.onDeploymentCompleted?.();
      return data;
    }

    if (data.status === Status.Failed || data.status === Status.Cancelled) {
      hooks?.onDeploymentFailed?.(data);
      throw new DeploymentCompletedVerificationError(
        data.error
          ? `Deployment ${data.status.toLocaleLowerCase()}, error: ${
              data.error
            }${
              data.startupTime && data.error.includes('exceeded CPU time limit')
                ? ` (Startup time: ${data.startupTime}ms exceeded CPU time limit)`
                : ''
            }`
          : `Deployment ${data.status.toLocaleLowerCase()}`,
      );
    }

    return handleInterval();
  };

  return check();
}
